import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import {addTicketAttachment} from '@shared/services/WorkflowServices';
import {Grid, Tooltip} from '@material-ui/core';
import TertiaryButton from '@components/system/ButtonField/TertiaryButton';
import DragAndDropAttachmentsDialog from './DragAndDropAttachmentsDialog/DragAndDropAttachmentsDialog';
import {IAttachmentsDetails} from '@shared/services/service-mappers/WorkflowMapper';
// import FileViewer from '../FileViewer/FileViewer';
import ViewMediaFile from 'src/modules/shared/ViewMediaFile/ViewMediaFile';
import {
  TICKETING_SYSTEM_COMMON_PERMISSIONS,
  TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER
} from '@shared/constants/Mapping';
import './TicketingSystemAttachments.scss';
import useTicketingSystemPermission, {
  IAllPossibleTicketModules,
  IGroupPermission
} from '../hooks/useTicketingSystemPermission';

interface ITicketingSystemAttachmentsProps {
  clinicGroupId: number;
  clinicId: number;
  patientTicketId: number;
  attachments: IAttachmentsDetails[];
  onAddAttachment: () => void;
  module: IAllPossibleTicketModules;
  groupPermission?: IGroupPermission;
}
const TicketingSystemAttachments = (
  props: ITicketingSystemAttachmentsProps
): JSX.Element => {
  const {
    clinicGroupId,
    clinicId,
    patientTicketId,
    attachments,
    onAddAttachment,
    module,
    groupPermission
  } = props;
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const [attachedFile, setAttachedFile] = useState<any>(null);
  const [openAttachmentDialog, setOpenAttachmentDialog] = useState<boolean>(
    false
  );
  const resetStateData = () => {
    setAttachedFile(null);
  };
  const attachFiles = async (data: any): Promise<void> => {
    if (!data) {
      enqueueSnackbar(
        t('TICKETING_SYSTEM.ATTACHMENTS.UPLOAD_FILE'),
        SnackBarConfig('e')
      );
      return;
    }
    try {
      const response = await addTicketAttachment({
        clinicGroupId,
        clinicId,
        patientTicketId,
        data,
        params: {
          module: TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[module]
        }
      });
      if (response) {
        enqueueSnackbar(response.message, SnackBarConfig('s'));
        resetStateData();
        onAddAttachment();
      }
    } catch (error: any) {
      enqueueSnackbar(error && error.message, SnackBarConfig('e'));
    }
  };

  // getInputfile attached one
  const getInputFile = (files: any): void => {
    const data = new FormData();
    const file = files[0];
    if (!file) {
      return setAttachedFile(null);
    }
    if (file.size > 10 * 1024 * 1024) {
      // Check if file size exceeds 5MB
      enqueueSnackbar(
        t('TICKETING_SYSTEM.ATTACHMENTS.SIZE_EXCEEDS'),
        SnackBarConfig('w')
      );
      setAttachedFile(null);
      return;
    } else if (file.size === 0) {
      enqueueSnackbar(
        t('TICKETING_SYSTEM.ATTACHMENTS.EMPTY_FILE'),
        SnackBarConfig('w')
      );
      return;
    }
    // TODO:
    // failed scenario: if the uploaded file is an image or any other file without any extension defined in the file name,
    // then the API is sending the content-Type as application/octet-stream, which causes an issue, need to address this /*
    data.append('inputname', file);
    setAttachedFile(data);
  };
  const renderAttachments = () => {
    if (attachments?.length > 0) {
      return (
        <Grid container spacing={1}>
          {attachments.map((item: any, index: number) => (
            <Grid item sm={12} md={8} lg={6} xl={4} key={index}>
              <ViewMediaFile
                clinicGroupId={clinicGroupId}
                clinicId={clinicId}
                fileId={item.id}
                extension={item?.extension}
                mimeType={item?.mime_type}
                fileName={item?.file_name}
                mainClass="transcribed-media-item"
                type="prior"
                ticketId={patientTicketId}
                module={module}
              />
            </Grid>
          ))}
        </Grid>
      );
    }
    return <p>{t('TICKETING_SYSTEM.ATTACHMENTS.NO_ATTACHMENTS')}</p>;
  };

  const hasPriorAuthAddAttachmentPermission = useTicketingSystemPermission({
    module,
    groupPermission,
    objectFunction: TICKETING_SYSTEM_COMMON_PERMISSIONS.CREATE_ATTACHMENT
  });

  return (
    <div className="ticketing-system-attachment-wrapper">
      <div className="main-wrapper-title">
        <div className="section-title">
          {t('TICKETING_SYSTEM.ATTACHMENTS.TITLE')}
        </div>
        {hasPriorAuthAddAttachmentPermission && (
          <Tooltip title={t('TICKETING_SYSTEM.ATTACHMENTS.ADD').toString()}>
            <TertiaryButton
              className="add-attachment-button"
              onClick={(e: any): void => {
                setOpenAttachmentDialog(true);
              }}
              startIcon={<AddCircleIcon />}>
              {t('TICKETING_SYSTEM.ATTACHMENTS.ADD')}
            </TertiaryButton>
          </Tooltip>
        )}
      </div>
      <div className="attachments-parent-wrapper">{renderAttachments()}</div>
      <DragAndDropAttachmentsDialog
        open={openAttachmentDialog}
        onClose={() => {
          setOpenAttachmentDialog(false);
        }}
        title={t('TICKETING_SYSTEM.ATTACHMENTS.ADD_ATTACHMENT')}
        file={getInputFile}
        attachFilesFn={attachFiles}
        attachedFile={attachedFile}
      />
    </div>
  );
};

export default TicketingSystemAttachments;
